import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  CalendarDays,
  MessageCircle,
  MessageSquare,
} from "lucide-react";
import HeaderDash from "../../components/HeaderDash";
import TripAvatar from "../../components/TripAvatar";
import bookingService from "../../api/services/bookingService";
import { useToast } from "../../hooks/useToast";
import { mapBookingToTrip } from "../../utils/myTrips";

// Disputes go to support by email with the booking named up front, so support
// can find it without a back-and-forth.
const SUPPORT_EMAIL = "support@pulohq.com";

const extractBooking = (response) => response?.data?.booking || response?.booking || null;

function DetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-3">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="text-right text-sm font-semibold text-gray-900">{value}</dd>
    </div>
  );
}

export default function TripDetails() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);

    bookingService
      .getBookingById(bookingId)
      .then((response) => {
        if (active) setBooking(extractBooking(response));
      })
      .catch((err) => {
        if (!active) return;
        setBooking(null);
        toast.apiError(err, "Unable to load this trip right now.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [bookingId, toast]);

  const trip = booking ? mapBookingToTrip(booking) : null;

  // Confirmation and cancellation responses carry the parties as bare ids, so
  // keep the populated ones already on screen.
  const mergeBooking = (updates) =>
    setBooking((prev) => ({
      ...prev,
      ...updates,
      user: prev?.user,
      assistant: prev?.assistant,
    }));

  const handleConfirm = async (phase) => {
    setConfirming(true);
    try {
      const response =
        phase === "start"
          ? await bookingService.confirmTripStart(bookingId)
          : await bookingService.confirmTripEnd(bookingId);

      // The server decides whether this confirmation was the second one; trust
      // the booking it returns rather than guessing the new status here.
      const updated = extractBooking(response);
      if (updated?._id) mergeBooking(updated);

      const bothDone =
        phase === "start"
          ? updated?.status === "trip_started"
          : updated?.status === "completed";
      toast.success(
        bothDone
          ? phase === "start"
            ? "Trip started."
            : "Trip completed."
          : "Confirmed. Waiting for your Pulo Assistant to confirm too."
      );
    } catch (err) {
      toast.apiError(err, "Unable to confirm right now. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  const handleCancel = async () => {
    if (
      !window.confirm(
        "Cancel this booking? You'll get everything back except Pulo's 20% commission."
      )
    )
      return;
    setCancelling(true);
    try {
      await bookingService.cancelBooking(bookingId);
      mergeBooking({ status: "cancelled" });
      toast.success("Booking cancelled.");
    } catch (err) {
      toast.apiError(err, "Unable to cancel this booking. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  const messageAssistant = () =>
    navigate("/user/messages", {
      state: {
        recipientId: trip.assistantId,
        assistantId: trip.assistantId,
        assistantName: trip.assistantName,
        assistantImage: trip.assistantImage,
      },
    });

  const raiseDispute = () => {
    const subject = `Dispute: trip with ${trip.assistantName} (${trip.reference})`;
    const body = `Booking reference: ${trip.reference}\n\nWhat went wrong:\n`;
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  const confirmPhase = trip?.canConfirmStart ? "start" : trip?.canConfirmEnd ? "end" : null;

  return (
    <div className="min-h-screen bg-white">
      <HeaderDash />

      <main className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6">
        <button
          type="button"
          onClick={() => navigate(trip ? `/user/trips?tab=${trip.tab}` : "/user/trips")}
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#61013B] transition hover:text-[#4f0f32]"
        >
          <ArrowLeft className="h-4 w-4" />
          My Trips
        </button>

        {loading ? (
          <p className="mt-10 rounded-2xl border border-gray-100 px-4 py-10 text-center text-sm font-medium text-gray-500">
            Loading trip…
          </p>
        ) : !trip ? (
          <div className="mt-10 rounded-2xl border border-dashed border-gray-200 px-4 py-10 text-center">
            <p className="text-lg font-semibold text-gray-900">We couldn't find this trip.</p>
            <p className="mt-1 text-sm font-medium text-gray-500">
              It may have been removed, or it belongs to another account.
            </p>
          </div>
        ) : (
          <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <section className="space-y-6">
              <div className="flex items-center gap-4">
                <TripAvatar
                  name={trip.assistantName}
                  image={trip.assistantImage}
                  size="h-16 w-16"
                />
                <div className="min-w-0 space-y-1">
                  <h1 className="truncate text-2xl font-bold text-[#61013B]">
                    {trip.assistantName}
                  </h1>
                  <p className="flex items-center gap-1.5 text-sm font-medium text-gray-500">
                    <CalendarDays className="h-4 w-4 shrink-0" />
                    {trip.dateRange}
                  </p>
                  <span className="inline-flex rounded-full bg-[#f2dfe9] px-3 py-1 text-xs font-semibold text-[#6b0c44]">
                    {trip.statusLabel}
                  </span>
                </div>
              </div>

              <dl className="divide-y divide-gray-100 rounded-2xl border border-gray-100">
                <DetailRow label="Booking reference" value={trip.reference} />
                <DetailRow label="Service dates" value={trip.dateRange} />
                <DetailRow label="Booked on" value={trip.bookedOn} />
              </dl>

              {trip.itinerary && (
                <div className="rounded-2xl border border-gray-100 p-5">
                  <h2 className="text-base font-semibold text-gray-900">Itinerary</h2>
                  <p className="mt-2 whitespace-pre-wrap text-sm font-medium leading-6 text-gray-600">
                    {trip.itinerary}
                  </p>
                </div>
              )}

              <div className="rounded-2xl border border-gray-100 p-5">
                <h2 className="text-base font-semibold text-gray-900">Cost breakdown</h2>
                <div className="mt-3 space-y-2 text-sm font-medium">
                  {trip.costBreakdown.map((row) => (
                    <div key={row.label} className="flex items-center justify-between gap-4">
                      <span className="text-gray-500">{row.label}</span>
                      <span className="text-gray-900">{row.amount}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3 text-base font-semibold text-[#61013B]">
                  <span>Total paid</span>
                  <span>{trip.total}</span>
                </div>
              </div>
            </section>

            <aside className="space-y-4">
              {confirmPhase && (
                <div className="space-y-3 rounded-2xl border border-[#F5D9A8] bg-[#FFF9EE] p-5">
                  <p className="text-base font-semibold text-gray-900">
                    {confirmPhase === "start" ? "Has your trip started?" : "Has your trip ended?"}
                  </p>
                  <p className="text-sm font-medium text-gray-600">
                    {confirmPhase === "start"
                      ? "Confirm once your Pulo Assistant is with you and the trip is under way. You and your assistant both need to confirm."
                      : "Confirm once the trip is over. You and your assistant both need to confirm."}
                  </p>
                  <button
                    type="button"
                    onClick={() => handleConfirm(confirmPhase)}
                    disabled={confirming}
                    className="w-full rounded-xl bg-[#61013B] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#4f0f32] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {confirming
                      ? "Confirming..."
                      : confirmPhase === "start"
                      ? "Confirm trip has started"
                      : "Confirm trip has ended"}
                  </button>
                </div>
              )}

              {trip.refundStatusNotice && (
                <div
                  className={`rounded-2xl px-5 py-4 text-sm font-medium ${
                    trip.refundStatus === "success"
                      ? "bg-[#E4F5E4] text-[#1B7A1B]"
                      : trip.refundStatus === "failed"
                      ? "bg-[#FDE8E8] text-[#C81E1E]"
                      : "bg-[#FFF6E5] text-[#B26700]"
                  }`}
                >
                  <p className="font-semibold">Refund</p>
                  <p className="mt-1 leading-5">{trip.refundStatusNotice}</p>
                </div>
              )}

              {(trip.awaitingAssistantStart || trip.awaitingAssistantEnd) && (
                <p className="rounded-2xl bg-[#FFF6E5] px-5 py-4 text-sm font-medium text-[#B26700]">
                  You have confirmed. Waiting for your Pulo Assistant to confirm.
                </p>
              )}

              {trip.assistantId && (
                <button
                  type="button"
                  onClick={messageAssistant}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#61013B] px-4 py-3 text-sm font-semibold text-[#61013B] transition hover:bg-[#f7f1f5]"
                >
                  <MessageSquare className="h-4 w-4" />
                  Message assistant
                </button>
              )}

              {/* Once the deadline passes the button goes but the line stays:
                  a traveller who came here to cancel needs to be told why it
                  is no longer there. */}
              {(trip.cancellable || trip.refundNotice) && (
                <div className="space-y-2">
                  {trip.cancellable && (
                    <button
                      type="button"
                      onClick={handleCancel}
                      disabled={cancelling}
                      className="w-full rounded-xl border border-red-300 px-4 py-3 text-sm font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {cancelling ? "Cancelling..." : "Cancel booking"}
                    </button>
                  )}
                  {trip.refundNotice && (
                    <p className="text-xs leading-5 text-gray-500">
                      {trip.refundNotice}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-3 rounded-2xl border border-gray-100 p-5">
                <p className="text-base font-semibold text-gray-900">Need help?</p>
                <button
                  type="button"
                  onClick={() => navigate("/user/contact")}
                  className="flex items-center gap-2 text-sm font-semibold text-[#61013B] underline decoration-2 underline-offset-4"
                >
                  <MessageCircle className="h-4 w-4" />
                  Contact support
                </button>
                <button
                  type="button"
                  onClick={raiseDispute}
                  className="flex items-center gap-2 text-sm font-semibold text-[#61013B] underline decoration-2 underline-offset-4"
                >
                  <AlertTriangle className="h-4 w-4" />
                  Raise a dispute
                </button>
              </div>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}
